// client/src/components/marketer/MarketerMyInfoCheck.js
import React, { useEffect, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const PROFILE_TTL_MS = 10 * 60 * 1000; // 10분

function formatPhone(v) {
  if (!v) return '-';
  const s = String(v).replace(/\D/g, '');
  if (s.length === 10) return s.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  if (s.length === 11) return s.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  return v;
}

// ✅ 동적으로 캐시 키 생성 (함수 호출 시마다 현재 값 읽음)
function getProfileCacheKey() {
  try {
    const userKey = String(sessionStorage.getItem('current_user_key') || '').toLowerCase();
    return userKey ? `mrk_profile__${userKey}` : '';
  } catch {
    return '';
  }
}

// ✅ 동적 캐시 읽기 (TTL + 사용자 검증)
function readCachedProfile() {
  const key = getProfileCacheKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.ts || !obj.data) return null;
    // TTL 체크
    if (Date.now() - obj.ts > PROFILE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    // 사용자 검증
    const currentUserKey = String(sessionStorage.getItem('current_user_key') || '').toLowerCase();
    if (obj.userKey && obj.userKey !== currentUserKey) {
      localStorage.removeItem(key);
      return null;
    }
    return obj.data;
  } catch {
    return null;
  }
}

// ✅ 캐시 저장 (사용자 키 포함)
function writeCachedProfile(data) {
  const key = getProfileCacheKey();
  if (!key) return;
  try {
    const userKey = String(sessionStorage.getItem('current_user_key') || '').toLowerCase();
    localStorage.setItem(key, JSON.stringify({ 
      ts: Date.now(), 
      data,
      userKey
    }));
  } catch {}
}

function UploadModal({ open, onClose, onUpdated, currentBizRegNo = '' }) {
  const [files, setFiles] = useState([]);
  const [bizRegNo, setBizRegNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (open) { 
      setFiles([]); 
      setBizRegNo(currentBizRegNo || '');
      setMsg(''); 
      setLoading(false); 
    }
  }, [open, currentBizRegNo]);

  if (!open) return null;

  const onPick = (e) => setFiles(Array.from(e.target.files || []));

  const onConfirm = async () => {
    if (loading) return;
    setMsg('');
    setLoading(true);
    try {
      // ✅ 파일도 없고 번호도 변경 없으면 에러
      const bizRegNoChanged = bizRegNo.trim() !== (currentBizRegNo || '').trim();
      if (!files.length && !bizRegNoChanged) { 
        setMsg('첨부할 파일을 선택하거나 사업자 등록번호를 변경해주세요.'); 
        setLoading(false); 
        return; 
      }
      
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      if (bizRegNo.trim()) {
        fd.append('bizRegNo', bizRegNo.trim());
      }
      
      const r = await fetch(`${API_URL}/api/marketer/myinfo/biz-certi/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.success) {
        throw new Error(j?.message || '업로드 실패');
      }
      
      // ✅ 결과 메시지 구성
      const msgs = [];
      if (j.count > 0) msgs.push(`파일 ${j.count}건 업로드`);
      if (j.bizRegNoUpdated) msgs.push('사업자 등록번호 업데이트');
      setMsg(msgs.length > 0 ? `${msgs.join(', ')} 완료` : '업데이트 완료');
      
      if (typeof onUpdated === 'function') await onUpdated();
      setTimeout(onClose, 500);
    } catch (e) {
      setMsg(e.message || '업로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mbm-overlay">
      <div className="mb-card mbm" style={{ width: 460, maxWidth: '90%', margin: 0, padding: 24 }}>
        <div className="mbm-head">
          <h3 className="mb-page-title mbm-title">사업자 정보 업데이트</h3>
          <button type="button" className="mbm-close" onClick={onClose} aria-label="닫기">✕</button>
        </div>
        <p className="mb-page-description" style={{ marginBottom: 16 }}>
          사업자 등록번호와 등록증 이미지를 업데이트하세요.
        </p>
        
        {/* ✅ 사업자 등록번호 입력 필드 */}
        <div className="mb-form-group">
          <label className="mb-form-label">사업자 등록번호</label>
          <input 
            type="text"
            value={bizRegNo}
            onChange={(e) => setBizRegNo(e.target.value)}
            placeholder="000-00-00000 (자유형식)"
            className="mb-form-control"
            style={{ marginBottom: 16 }}
          />
        </div>
        
        {/* ✅ 파일 업로드 필드 */}
        <div className="mb-form-group">
          <label className="mb-form-label">사업자 등록증 첨부 (선택)</label>
          <input 
            type="file" 
            multiple 
            accept=".jpg,.jpeg,.png,.webp,.pdf" 
            onChange={onPick}
            className="mb-form-control"
            style={{ padding: '8px' }}
          />
          <p style={{ fontSize: 12, color: 'var(--mb-text-sub)', marginTop: 4 }}>
            이미지(jpg, png, webp) 또는 PDF 파일
          </p>
        </div>

        {msg && (
          <div style={{ 
            color: msg.includes('실패') || msg.includes('오류') ? 'var(--mb-color-danger)' : 'var(--mb-color-primary)', 
            fontSize: 14, 
            marginBottom: 16 
          }}>
            {msg}
          </div>
        )}

        <div className="mbm-foot">
          <button className="mb-btn mb-btn-secondary" onClick={onClose} disabled={loading}>취소</button>
          <button className="mb-btn mb-btn-primary" onClick={onConfirm} disabled={loading}>
            {loading ? '업데이트 중...' : '확인'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ✅ 스켈레톤 로딩 UI 컴포넌트
function ProfileSkeleton() {
  const skLabel = { height: 12, width: '35%', background: '#eceef2', borderRadius: 4, marginBottom: 8 };
  const skValue = {
    height: 22,
    background: '#f6f8fb',
    border: '1px solid #eceef2',
    borderRadius: 8,
    animation: 'pulse 1.5s ease-in-out infinite'
  };
  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">계정 정보</h2>
        <p className="mb-page-description">정보를 불러오는 중...</p>
      </div>

      {/* 기본 정보 카드 */}
      <div className="mb-card">
        <h3 className="mb-section-title">기본 정보</h3>
        <div className="mb-grid-3">
          {[1, 2, 3].map(i => (
            <div key={i}>
              <div style={skLabel} />
              <div style={skValue} />
            </div>
          ))}
        </div>
      </div>

      {/* 사업자 정보 카드 */}
      <div className="mb-card">
        <h3 className="mb-section-title">사업자 정보</h3>
        <div style={{ ...skLabel, width: '25%' }} />
        <div style={{ ...skValue, width: '50%' }} />
      </div>

      {/* 연락처 정보 카드 */}
      <div className="mb-card">
        <h3 className="mb-section-title">연락처 정보</h3>
        <div className="mb-grid-2">
          {[1, 2].map(i => (
            <div key={i}>
              <div style={skLabel} />
              <div style={{ ...skValue, height: 48 }} />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default function MarketerMyInfoCheck() {
  // ✅ 초기 캐시 읽기 (컴포넌트 마운트 시점)
  const [profile, setProfile] = useState(() => readCachedProfile());
  const [loading, setLoading] = useState(!profile);  // ✅ 캐시 없으면 로딩
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ 프로필 조회 함수
  const fetchProfile = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/marketer/mybiz/profile`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j?.ok && j?.profile) {
        setProfile(j.profile);
        writeCachedProfile(j.profile);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    // 캐시가 없으면 로딩 표시하며 조회, 있으면 백그라운드 갱신
    fetchProfile(!profile);
    
    // 주기적 갱신
    const t = setInterval(() => fetchProfile(false), PROFILE_TTL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProfile]);

  // ✅ 로딩 중일 때 스켈레톤 UI 표시
  if (loading && !profile) {
    return <ProfileSkeleton />;
  }

  // 프로필이 없으면 (로딩 완료 후에도) 빈 상태
  if (!profile) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">계정 정보</h2>
          <p className="mb-page-description">등록된 광고주 정보를 확인하고 사업자 등록증을 업데이트할 수 있습니다.</p>
        </div>
        <div className="mb-card">
          <div className="note note--red" style={{ marginTop: 0 }}>프로필 정보를 불러올 수 없습니다.</div>
        </div>
      </div>
    );
  }

  const TYPE_LABELS = { corporate: '법인 사업자', sole: '개인 사업자', agency: '광고 대행사', freelancer: '프리랜서' };
  const displayName = profile.businessName || profile.companyName || '-';
  const typeLabel = TYPE_LABELS[profile.type] || profile.type || '-';
  const phone = formatPhone(profile.callNumber);
  const email = profile.email || '-';
  const bizNo = profile.bizRegNo ? String(profile.bizRegNo) : '';

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">계정 정보</h2>
        <p className="mb-page-description">등록된 광고주 정보를 확인하고 사업자 등록증을 업데이트할 수 있습니다.</p>
      </div>

      {/* 카드 1: 기본 정보 (라벨-값) */}
      <div className="mb-card">
        <h3 className="mb-section-title">기본 정보</h3>
        <div className="mb-grid-3">
          <div>
            <div className="t-sub">상호명</div>
            <div className="t-name" style={{ marginTop: 3 }}>{displayName}</div>
          </div>
          <div>
            <div className="t-sub">담당자</div>
            <div className="t-name" style={{ marginTop: 3 }}>{profile.userName || '-'}</div>
          </div>
          <div>
            <div className="t-sub">광고주 유형</div>
            <div className="t-name" style={{ marginTop: 3 }}>{typeLabel}</div>
          </div>
        </div>
        <p className="mb-info-hint" style={{ marginTop: 12 }}>회원가입 시 등록된 정보입니다. 변경은 문의하기를 통해 진행해주세요.</p>
      </div>

      {/* 카드 2: 사업자 정보 */}
      <div className="mb-card">
        <h3 className="mb-section-title">사업자 정보</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <div className="t-sub">사업자 등록번호</div>
            <div className="t-name" style={{ marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
              {bizNo || '사업자등록 번호를 업데이트 해주세요'}
            </div>
          </div>
          <button
            className="mb-btn mb-btn-secondary"
            onClick={() => setModalOpen(true)}
          >
            등록증 업데이트
          </button>
        </div>
        <p className="mb-info-hint" style={{ marginTop: 12 }}>'등록증 업데이트'에서 등록한 사업자등록증과 등록번호 기준입니다.</p>
      </div>

      {/* 카드 3: 연락처 정보 */}
      <div className="mb-card">
        <h3 className="mb-section-title">연락처 정보</h3>
        <div className="mb-grid-2">
          <div className="mb-form-group" style={{ marginBottom: 0 }}>
            <label className="mb-form-label">등록된 휴대폰 번호</label>
            <input type="text" className="mb-form-control" value={phone} disabled readOnly />
            <p className="mb-info-hint">휴대폰 변경은 문의하기를 통해 진행해주세요.</p>
          </div>

          <div className="mb-form-group" style={{ marginBottom: 0 }}>
            <label className="mb-form-label">등록된 이메일</label>
            <input type="text" className="mb-form-control" value={email} disabled readOnly />
            <p className="mb-info-hint">이메일 변경은 문의하기를 통해 진행해주세요.</p>
          </div>
        </div>
      </div>

      <UploadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpdated={() => fetchProfile(false)}
        currentBizRegNo={bizNo}
      />
    </div>
  );
}
